/* eslint-disable array-bracket-newline */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import BigNumber from 'bignumber.js';
import { ITicker, Network } from '@kattana-io/common';
import Text from '../../../../atoms/Text';
import Image from '../../../../atoms/Image';
import TradingBox from '../../../../molecules/TradingBox';
import ParamField from '../../common/ParamField';
import GraphTooltip from '../../common/GraphTooltip';
import GasPanel from '../../common/GasPanel';
// import stop_price_graph from '../../images/stop_price.png';
// import limit_price_graph from '../../images/limit_price.png';
// import add_grid_graph from '../../images/add_grid.png';
import big_arrow from '../../../../assets/images/arrow_down.svg';
import { LimitActions } from '../../reducers/dex/limitReducer';
import { getPercentFromBalance } from '../../helpers/balances';
import { getLimitFee } from '../../helpers/statistics';
import { actionShowModal } from '../../../../actions/modal';
import { EOverlays, ETabs, ICoin } from '../../reducers/dex/tradingReducer';
import { ModalTypes } from '../../../../reducers/modal';
import { validateSettingsValue } from '../../helpers/settingsFormatters';
import { OnRefresh } from '../../../../wallet/walletContext';
import { WalletTokenBalancesBySym } from '../../../../wallet/walletReducer';
import { getOrderCondition } from '../../helpers/limitOrder';
import useLimit from './useLimit';
import LimitCreateButton from '../../common/LimitCreateButton';
import ExpandableParamField from '../../common/ExpandableParamField';
import { formatToRawNumber } from '../../../../helpers/truncate';

export interface ILimitProps {
  tab: ETabs;
  setTab: (val: ETabs) => void;
  setOverlay: (overlay: EOverlays) => void;
  chainId: number;
  base: ICoin;
  quote: ICoin;
  meta?: ITicker;
  account: string | null | undefined;
  tokenBalancesBySym: WalletTokenBalancesBySym;
  ratesAddress: any;
  current: ITicker;
  onFlip: () => void;
  onSetBaseAmount: (input: BigNumber) => void;
  onSetQuoteAmount: (input: BigNumber) => void;
  onSetTradesCount: (input: number) => void;
  onSetSlippage: (val: number) => void;
  onSetDeadline: (val: number) => void;
  onSetSendTokens: (val: boolean) => void;
  onNotify: (input: any) => void;
  onWalletRefresh: OnRefresh;
  deadline: number;
  slippage: number;
  tradesCount: number;
  sendTokens: boolean;
  tradingNetwork: Network;
}

const Limit = (props: ILimitProps) => {
  const appDispatch = useDispatch();
  const {
    base,
    quote,
    account,
    meta,
    setTab,
    onFlip,
    onNotify,
    onSetTradesCount,
    onSetSlippage,
    onSetDeadline,
    onSetSendTokens,
    onWalletRefresh,
    slippage,
    deadline,
    sendTokens,
    tradingNetwork,
    tab,
  } = props;

  const widget = useRef<HTMLDivElement>(null);

  const {
    limitTab,
    network,
    isSameChain,
    baseBalance,
    quoteBalance,
    // limitMode,
    stopPrice,
    tradesCount,
    isGrid,
    grid,
    quoteName,
    nativeToken,
    proxyWallet,
    basePriceUSD,
    quotePriceUSD,
    advancedSettings,
    valid,
    loading,
    onBaseUpdate,
    onQuoteUpdate,
    onDeployRequested,
    onCreateOrder,
    fetchNativeTokenBalance,
    dispatch,
  } = useLimit(props);

  useEffect(() => {
    if (isGrid) {
      onSetTradesCount(2);
    } else {
      onSetTradesCount(1);
    }
  }, [isGrid]);

  const { canCreate, label } = getOrderCondition(
    tab,
    base,
    quote,
    valid,
    loading,
    nativeToken.amount,
    meta
  );

  const renderContent = () => {
    return (
      <>
        <TradingBox
          network={tradingNetwork}
          disabled={false}
          title='Send'
          balance={baseBalance}
          coinAddress={base.address}
          coinTitle={base.name}
          value={base.amount}
          onChange={onBaseUpdate}
          setTab={() => props.setOverlay(EOverlays.BaseCurrencyMenu)}
          valueUSD={base.amount.multipliedBy(basePriceUSD).toNumber()}
          coinFullName={base.symbol}
          isCoinChangable
        />
        <div className='row percents'>
          <Text
            size={12}
            className={`percents__item ${
              !base.amount.eq(0) &&
              base.amount.eq(getPercentFromBalance(baseBalance, 0.2))
                ? 'percents__item--active'
                : ''
            }`}
            onClick={() =>
              onBaseUpdate(getPercentFromBalance(baseBalance, 0.2))
            }
          >
            20%
          </Text>
          <Text
            size={12}
            className={`percents__item ${
              !base.amount.eq(0) &&
              base.amount.eq(getPercentFromBalance(baseBalance, 0.35))
                ? 'percents__item--active'
                : ''
            }`}
            onClick={() =>
              onBaseUpdate(getPercentFromBalance(baseBalance, 0.35))
            }
          >
            35%
          </Text>
          <div className='big_arrow'>
            <Image src={big_arrow} size='s' onClick={onFlip} />
          </div>
          <Text
            size={12}
            className={`percents__item ${
              !base.amount.eq(0) &&
              base.amount.eq(getPercentFromBalance(baseBalance, 0.5))
                ? 'percents__item--active'
                : ''
            }`}
            onClick={() =>
              onBaseUpdate(getPercentFromBalance(baseBalance, 0.5))
            }
          >
            50%
          </Text>
          <Text
            size={12}
            className={`percents__item ${
              !base.amount.eq(0) &&
              base.amount.eq(getPercentFromBalance(baseBalance, 0.65))
                ? 'percents__item--active'
                : ''
            }`}
            onClick={() =>
              onBaseUpdate(getPercentFromBalance(baseBalance, 0.65))
            }
          >
            65%
          </Text>
        </div>
        <TradingBox
          network={tradingNetwork}
          disabled={false}
          title='Receive'
          balance={quoteBalance}
          coinAddress={quote.address}
          coinTitle={quote.name}
          value={quote.amount}
          onChange={onQuoteUpdate}
          setTab={() => props.setOverlay(EOverlays.QuoteCurrencyMenu)}
          valueUSD={quote.amount.multipliedBy(quotePriceUSD).toNumber()}
          coinFullName={quote.symbol}
          isCoinChangable
        />
        <div className='tab_selector'>
          {!tradingNetwork.isARTHERA() && (
            <div className='tab' onClick={() => setTab(ETabs.Swap)}>
              <Text size={12}>Market</Text>
            </div>
          )}
          <div className='tab tab--active' onClick={() => setTab(ETabs.Limit)}>
            <Text size={12}>Limit</Text>
          </div>
          <div className='tab' onClick={() => setTab(ETabs.WhaleLimit)}>
            <Text size={12}>Whale Limit</Text>
          </div>
        </div>
        <GasPanel
          chainId={network?.toDecimal()}
          proxyAddress={proxyWallet}
          onRefetchBalances={() => {
            fetchNativeTokenBalance();
            onWalletRefresh([]);
          }}
          nativeToken={nativeToken}
          onNotify={onNotify}
          limitTab={limitTab}
          onManageProxy={() =>
            appDispatch(actionShowModal(ModalTypes.limitOrderProxy, {}))
          }
        />
        <ParamField
          title='Stop price'
          value={formatToRawNumber(7, false, stopPrice)}
          onSave={(value) =>
            dispatch({
              type: LimitActions.SetStopPrice,
              payload: value,
            })
          }
          getTooltip={() => (
            <GraphTooltip
              title='Stop price'
              description="Price from which we're starting our trading session"
              // imageUrl={stop_price_graph}
              // height={157}
            />
          )}
          ending={quoteName}
          extended
        />
        <div
          className={`advanced_settings ${
            advancedSettings ? 'advanced_settings--active' : ''
          }`}
          onClick={() =>
            dispatch({ type: LimitActions.ToggleAdvancedSettings })
          }
        >
          <Text size={14}>Advanced Settings</Text>
          <div className='advanced_settings__icon icon-chevron-down' />
        </div>
        {advancedSettings && (
          <div className='params_panel'>
            <ExpandableParamField
              title={isGrid ? 'Remove Grid' : 'Add Grid'}
              expanded={isGrid}
              onExpand={() => {
                dispatch({ type: LimitActions.ToggleGrid });
              }}
              getTooltip={() => (
                <GraphTooltip
                  title='Add grid'
                  description='Plan multiple trades with different prices within one order'
                  // imageUrl={add_grid_graph}
                  // height={184}
                />
              )}
            />
            {isGrid && (
              <div className='params_panel__expanded'>
                <ParamField
                  title={`Initial price (${quoteName})`}
                  value={grid.initPrice.toFixed(4)}
                  onSave={(value) =>
                    dispatch({
                      type: LimitActions.SetGridInitPrice,
                      payload: value,
                    })
                  }
                  isNew
                />
                <ParamField
                  title={`Final price (${quoteName})`}
                  value={grid.finalPrice.toFixed(4)}
                  onSave={(value) =>
                    dispatch({
                      type: LimitActions.SetGridFinalPrice,
                      payload: value,
                    })
                  }
                  isNew
                />
              </div>
            )}
            <div className='params_panel__extra_settings'>
              <ParamField
                title='Max swaps for order'
                value={tradesCount.toFixed(0)}
                onSave={onSetTradesCount}
                validateValue={(value) => {
                  const isValid = validateSettingsValue(value, 1, 99, true);
                  dispatch({
                    type: LimitActions.SetValid,
                    payload: { tradesCount: isValid },
                  });
                  return isValid;
                }}
                isError={!valid.tradesCount}
                short
                isNew
              />
              <ParamField
                title='Slippage'
                value={slippage.toString()}
                onSave={onSetSlippage}
                validateValue={(value) => {
                  const isValid = validateSettingsValue(value, 0.5, 50);
                  dispatch({
                    type: LimitActions.SetValid,
                    payload: { slippage: isValid },
                  });
                  return isValid;
                }}
                getTooltip={() => (
                  <GraphTooltip
                    title='Slippage (0.5 - 50)'
                    description="Difference between the expected and actual price of a trade on a DEX. It's influenced by factors like low liquidity and market volatility"
                  />
                )}
                isError={!valid.slippage}
                ending='%'
                short
                isNew
              />
              <ParamField
                title='Order deadline (days)'
                value={deadline.toString()}
                onSave={onSetDeadline}
                validateValue={(value) => {
                  const isValid = validateSettingsValue(value, 1, 30, true);
                  dispatch({
                    type: LimitActions.SetValid,
                    payload: { deadline: isValid },
                  });
                  return isValid;
                }}
                getTooltip={() => (
                  <GraphTooltip
                    title='Deadline (1 - 30)'
                    description='Days before the order will be automatically canceled'
                  />
                )}
                isError={!valid.deadline}
                short
                isNew
              />
              <ParamField
                title='Send tokens to proxy'
                value={sendTokens ? 'true' : 'false'}
                onSave={() => onSetSendTokens(!sendTokens)}
                getTooltip={() => (
                  <GraphTooltip
                    title='Deadline'
                    description='Transfer tokens to proxy contract when creating a limit order'
                  />
                )}
                isCheckbox
                isNew
              />
            </div>
          </div>
        )}
        <div className='btn_margin'>
          <LimitCreateButton
            limitTab={limitTab}
            createCondition={!canCreate}
            onCreate={onCreateOrder}
            label={label}
            chainId={network.toDecimal()}
            isCorrectChain={isSameChain}
            onDeployRequested={onDeployRequested}
            account={account}
          />
        </div>
        <div className='total_fees'>
          <Text size={12}>Approx. total fees</Text>
          <Text size={14}>
            {getLimitFee(tradesCount)} {nativeToken.name}
          </Text>
        </div>
      </>
    );
  };

  return (
    <div className='container' ref={widget}>
      {renderContent()}
    </div>
  );
};

export default Limit;
